import { useState, useEffect, forwardRef } from 'react';
import './Calendar.scss';
import { IoIosArrowBack, IoIosArrowForward } from 'react-icons/io';
import axios from 'axios';
import '../../../pages/consultation/ConsultationStep.scss';
import Loader from '../../../loader/Loader';

const Calendar = forwardRef(({
  selectedDate,
  MeasurementServiceDateforPlanning,
  setSelectedDate,
  selectedTime,
  setSelectedSlot,
  serviceType,
  storeLocation,
  uniqueID,


}, ref) => {
  const today = new Date();
  const [currentMonth, setCurrentMonth] = useState(today.getMonth());
  const [currentYear, setCurrentYear] = useState(today.getFullYear());

  const [validSelectableDates, setValidSelectableDates] = useState([]);
  const [timeSlotMap, setTimeSlotMap] = useState({});
  const [calendarLoading, setCalendarLoading] = useState(true);
  const [slotConfirming, setSlotConfirming] = useState(null);
  const [confirmedSlot, setConfirmedSlot] = useState(null);
  const [bookingWindowDays, setBookingWindowDays] = useState(null);
  const [expiryTime, setExpiryTime] = useState(null);
  const [configLoaded, setConfigLoaded] = useState(false);

  const daysOfWeek = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  useEffect(() => {
    const fetchBookingWindowDays = async () => {
      try {
        const response = await axios.get('/server/ikea_services_function/bookingwindowdays');
        const daysRaw = response.data?.data?.bookingwindowdays;
        const days = Number(daysRaw);

if (Number.isInteger(days) && days > 0 && days <= 60) {
  setBookingWindowDays(days);
}
      } catch (err) {
        console.error("Error fetching booking window days:", err);
      }
    };

    const fetchExpiryTime = async () => {
      try {
        const response = await axios.get('/server/ikea_services_function/bookingexpiry');
        const expiryRaw = response.data?.data?.bookingExpiry?.TimeinMinutes;
const expiry = Number(expiryRaw);


if (Number.isInteger(expiry) && expiry > 0 && expiry <= 1440) {
  setExpiryTime(expiry);
}
      } catch (err) {
        console.error("Error fetching expiry time:", err);
      }
    };

    Promise.all([fetchBookingWindowDays(), fetchExpiryTime()]).then(() => {
      setConfigLoaded(true);

    });
  }, []);

  const generateSelectableDates = (baseDateInput) => {
  const dates = [];

  let baseDate;
  if (baseDateInput) {
    const inputDate = new Date(baseDateInput);
    const todayDateOnly = new Date();
    todayDateOnly.setHours(0, 0, 0, 0);

    if (inputDate < todayDateOnly) {
      baseDate = todayDateOnly;
    } else {
      baseDate = inputDate;
    }
  } else {
    baseDate = new Date();
  }

  const startOffset = baseDateInput ? 2 : 1;

  for (let i = startOffset; i <= bookingWindowDays + startOffset - 1; i++) {
    const d = new Date(baseDate);
    d.setDate(d.getDate() + i);

    const formattedDate = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
      d.getDate()
    ).padStart(2, '0')}`;

    dates.push(formattedDate);
  }

  return dates;
};


  useEffect(() => {

  if (!configLoaded || !bookingWindowDays) return;

  const dates = generateSelectableDates(MeasurementServiceDateforPlanning);
  setValidSelectableDates(dates);

  if (dates.length > 0) {
    const firstDate = new Date(dates[0]);
    setCurrentMonth(firstDate.getMonth());
    setCurrentYear(firstDate.getFullYear());
  }
}, [configLoaded, bookingWindowDays, MeasurementServiceDateforPlanning]);

  const formatDateToZoho = (dateStr) => {
  const d = new Date(dateStr);
  const day = String(d.getDate()).padStart(2, '0');
  const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const month = monthNames[d.getMonth()];
  const year = d.getFullYear();

  return `${day}-${month}-${year}`;
};


  useEffect(() => {
    const fetchAllSlots = async () => {
      if (!serviceType?.ServiceName || !storeLocation?.StoreLocation || !uniqueID) return;

      setCalendarLoading(true);

      // Sequential fetching — Zoho Creator's public API rate-limits burst requests,
      // so parallel Promise.all causes most calls to fail silently with empty slots.
      const newMap = {};
      for (const dateKey of validSelectableDates) {
        try {
          const formattedDate = formatDateToZoho(dateKey);
          const resp = await axios.post('/server/creator_api_proxy', {
            action: 'fetchSlots',
            Service_Type: serviceType.ServiceName,
            Store_Location_string: storeLocation.StoreLocation,
            Selected_Date: formattedDate,
            UniqueID: uniqueID
          });
          const slots = Array.isArray(resp?.data?.data) ? resp.data.data : [];
          newMap[dateKey] = slots;
        } catch (e) {
          console.error(`[slots] ${dateKey} fetch failed:`, e.response?.data || e.message);
          newMap[dateKey] = [];
        }
      }

      setTimeSlotMap(newMap);
      setCalendarLoading(false);
    };

    if (validSelectableDates.length > 0 && configLoaded) {
      fetchAllSlots();
    }
  }, [validSelectableDates, serviceType, storeLocation, configLoaded, uniqueID]);

  // getDay() returns 0 for Sunday; || 7 remaps it so weeks start on Monday (Mon=1 … Sun=7)
  const getFirstDayOfMonth = (m, y) => new Date(y, m, 1).getDay() || 7;
  const getDaysInMonth = (m, y) => new Date(y, m + 1, 0).getDate();

  const prevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  const nextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  const formatDate = (day) =>
    `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

  const isSelected = (day) => selectedDate === formatDate(day);

  const handleSelect = (day) => {
    const dateKey = formatDate(day);
    if (!validSelectableDates.includes(dateKey)) return;
    setSelectedDate(dateKey);
    setSelectedSlot('');
    setConfirmedSlot(null);
  };

  const handleTimeSlotClick = async (slot) => {
    setSelectedSlot(slot);
    setSlotConfirming(slot);

    try {
      const resp = await axios.post('/server/creator_api_proxy', {
        action: 'TempSlotBlock',
        Selected_Date: formatDateToZoho(selectedDate),
        Service_Type: serviceType.ServiceName,
        Store_Location_string: storeLocation.StoreLocation,
        Time_Slot: slot,
        UniqueID: uniqueID,

      });

      if (resp?.data?.data === "Success") {
        setConfirmedSlot(slot);
      }
    } catch (error) {
      console.error('Error blocking slot:', error.response?.data || error.message);
    }

    setSlotConfirming(null);
  };

  const firstDay = getFirstDayOfMonth(currentMonth, currentYear);
  const totalDays = getDaysInMonth(currentMonth, currentYear);
  const monthName = new Date(currentYear, currentMonth).toLocaleDateString('default', {
    month: 'long',
  });

  const daysArray = [];
  for (let i = 1; i < firstDay; i++) daysArray.push(null);
  for (let i = 1; i <= totalDays; i++) daysArray.push(i);

  return (
    <>
      {calendarLoading ? (
        <div className="calendar-loader">
          <Loader />
        </div>
      ) : (
        <>
          <div className="calendar-container" ref={ref}>
            <div className="calendar-header">
              <button onClick={prevMonth}><IoIosArrowBack /></button>
              <h2>{monthName} {currentYear}</h2>
              <button onClick={nextMonth}><IoIosArrowForward /></button>
            </div>

            <div className="calendar-days">
              {daysOfWeek.map((d) => (
                <div key={d} className="calendar-day-label">{d}</div>
              ))}
            </div>

            <div className="calendar-grid">
              {daysArray.map((day, i) => {
                const dateKey = day ? formatDate(day) : null;
                const hasSlots = dateKey && timeSlotMap[dateKey]?.length > 0;
                const isElig = dateKey && validSelectableDates.includes(dateKey);
                return (
                  <div key={i} className="calendar-cell">
                    {day ? (
                      <div
                        onClick={() => handleSelect(day)}
                        className={[
                          'calendar-date',
                          !isElig ? 'disabled' : '',
                          isSelected(day) ? 'selected' : '',
                          isElig && hasSlots ? 'available' : '',
                        ].join(' ')}
                      >
                        <span>{day}</span>
                        {isElig && hasSlots && (
        <span
            className={`calendar-dot ${isSelected(day) ? 'dot-selected' : 'dot-available'}`}
            data-tooltip={`${timeSlotMap[dateKey].length} slots available`}
        />
    )}
                      </div>
                    ) : (
                      <div className="calendar-placeholder" />
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {selectedDate && (
            <div className="form-group">
              <label className="required">Select a time slot</label>
              <div className="slot-time">
                {timeSlotMap[selectedDate]?.length > 0 ? (
                  timeSlotMap[selectedDate].map((slot) => (
                    <button
                      key={slot}
                      className={`slot ${selectedTime === slot ? 'active' : ''}`}
                      onClick={() => handleTimeSlotClick(slot)}
                      disabled={slotConfirming !== null}
                    >
                      {slot}
                      {slotConfirming === slot && <span className="mini-loader" />}
                      {confirmedSlot === slot && slotConfirming === null && (
                        <span className="tick">&#10003;</span>
                      )}
                    </button>
                  ))
                ) : (
                  <p className="no-slots">No available slots for this date</p>
                )}
              </div>

              {selectedTime && expiryTime && (
                <p className="slot-warning-message">
                  Please complete the booking within {expiryTime} minutes to block this slot.
                </p>
              )}
            </div>
          )}
        </>
      )}
    </>
  );
});
export default Calendar;
